import React from 'react';
import {
  Dialog, DialogContent, Box, Typography, Button,
  LinearProgress, Fade,
} from '@mui/material';
import { AccessTime as ClockIcon, LockOutlined as LockIcon } from '@mui/icons-material';

interface Props {
  open: boolean;
  secondsLeft: number;
  onStayLoggedIn: () => void;
  onLogoutNow: () => void;
}

const InactivityWarningDialog: React.FC<Props> = ({ open, secondsLeft, onStayLoggedIn, onLogoutNow }) => {
  const pct = Math.round((secondsLeft / 30) * 100);
  const urgent = secondsLeft <= 10;

  return (
    <Dialog
      open={open}
      maxWidth="xs"
      fullWidth
      disableEscapeKeyDown
      onClose={() => {}} // non-dismissible
      PaperProps={{
        sx: {
          borderRadius: 4,
          textAlign: 'center',
          p: 1,
          boxShadow: '0 25px 60px rgba(0,0,0,0.18)',
          border: '1px solid #E7DECB',
        },
      }}
    >
      <DialogContent sx={{ pt: 4, pb: 3 }}>
        <Fade in timeout={400}>
          <Box>
            {/* Icon */}
            <Box sx={{
              width: 72, height: 72, borderRadius: '50%', mx: 'auto', mb: 2.5,
              background: urgent
                ? 'linear-gradient(135deg, #7D1935 0%, #a82846 100%)'
                : 'linear-gradient(135deg, #1A5F7A 0%, #2e86ab 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: urgent ? '0 8px 24px rgba(125,25,53,0.3)' : '0 8px 24px rgba(26,95,122,0.3)',
              transition: 'all 0.4s ease',
            }}>
              <ClockIcon sx={{ fontSize: 36, color: '#fff' }} />
            </Box>

            {/* Heading */}
            <Typography sx={{
              fontFamily: '"Playfair Display", serif',
              fontWeight: 700, fontSize: 22, mb: 1,
              color: urgent ? '#7D1935' : '#1A1A1A',
              transition: 'color 0.3s',
            }}>
              Still there?
            </Typography>

            <Typography sx={{ fontSize: 14, color: '#5D6D7E', mb: 3, lineHeight: 1.6 }}>
              You've been inactive. For your security, this session will end automatically.
            </Typography>

            {/* Countdown */}
            <Box sx={{
              bgcolor: urgent ? '#FBE3E3' : '#F0F7FA',
              borderRadius: 3, p: 2.5, mb: 3,
              border: `1px solid ${urgent ? '#F5C6CB' : '#C7DDE8'}`,
              transition: 'all 0.3s',
            }}>
              <Typography sx={{
                fontFamily: '"Playfair Display", serif',
                fontWeight: 800,
                fontSize: 52,
                lineHeight: 1,
                color: urgent ? '#7D1935' : '#1A5F7A',
                transition: 'color 0.3s',
              }}>
                {secondsLeft}
              </Typography>
              <Typography sx={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.8px', color: '#5D6D7E', mt: 0.5 }}>
                seconds remaining
              </Typography>
              <LinearProgress
                variant="determinate"
                value={pct}
                sx={{
                  mt: 1.5, height: 6, borderRadius: 3,
                  bgcolor: urgent ? 'rgba(125,25,53,0.12)' : 'rgba(26,95,122,0.12)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 3,
                    bgcolor: urgent ? '#7D1935' : '#1A5F7A',
                    transition: 'background-color 0.3s',
                  },
                }}
              />
            </Box>

            {/* Actions */}
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={onStayLoggedIn}
              sx={{
                mb: 1.5, py: 1.4, borderRadius: 2.5, textTransform: 'none',
                fontWeight: 700, fontSize: 15,
                bgcolor: '#1A5F7A',
                '&:hover': { bgcolor: '#154e64' },
              }}
            >
              Stay logged in
            </Button>
            <Button
              fullWidth
              variant="text"
              size="small"
              onClick={onLogoutNow}
              startIcon={<LockIcon sx={{ fontSize: 14 }} />}
              sx={{ color: '#5D6D7E', textTransform: 'none', fontSize: 13, '&:hover': { color: '#7D1935' } }}
            >
              Log out now
            </Button>

            {/* Footer */}
            <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #E7DECB' }}>
              <Typography sx={{ fontSize: 11, color: '#9DA8B2', letterSpacing: '.3px' }}>
                Secured by LifeScribe Admin · Session timeout: 10 min
              </Typography>
            </Box>
          </Box>
        </Fade>
      </DialogContent>
    </Dialog>
  );
};

export default InactivityWarningDialog;
